import React, { useState } from 'react';
import { ChartSetList, SavedChartState } from '../../types';
import { useFeatureAccess } from '../../contexts/SubscriptionContext';

interface ChartPickerProps {
  collection: ChartSetList;
  savedChartsData: SavedChartState[];
  onAddChart: (collectionId: string, chartTitle: string) => void;
}

const ChartPicker: React.FC<ChartPickerProps> = ({
  collection,
  savedChartsData,
  onAddChart
}) => {
  const { canUse, upgrade } = useFeatureAccess();
  const [chartFilter, setChartFilter] = useState<string>('');
  const [styleFilter, setStyleFilter] = useState<string>('All Styles');

  const handleAddChart = (chartTitle: string) => {
    // Check free tier limits
    if (!canUse('unlimitedSetlists') && collection.chartTitles.length >= 5) {
      upgrade('unlimitedSetlists');
      return;
    }
    onAddChart(collection.id, chartTitle);
  };

  const filteredCharts = savedChartsData
    .filter(chart => !collection.chartTitles.includes(chart.songTitle))
    .filter(chart => chart.songTitle.toLowerCase().includes(chartFilter.toLowerCase()))
    .filter(chart => styleFilter === 'All Styles' || chart.songStyle === styleFilter);

  return (
    <div className="flex-none sm:flex-1 flex flex-col max-h-[48vh] sm:max-h-none overflow-hidden">
      <div className="flex-shrink-0 mb-1">
        {!canUse('unlimitedSetlists') && collection.chartTitles.length >= 5 && (
          <div className="bg-gradient-to-r from-purple-600/10 to-blue-600/10 border border-purple-500/20 rounded-lg p-1.5 mb-1.5">
            <p className="text-xs text-gray-300 mb-1">Free limit (5 songs)</p>
            <button
              onClick={() => upgrade('unlimitedSetlists')}
              className="text-xs px-2 py-0.5 bg-purple-600 hover:bg-purple-500 text-white rounded font-semibold transition-colors"
            >
              Upgrade
            </button>
          </div>
        )}
        
        <div className="flex items-center gap-2 mb-1.5">
          <h4 className="text-sm sm:text-lg font-semibold text-white whitespace-nowrap">Add Charts</h4>
          <input
            type="text"
            placeholder="Filter..."
            value={chartFilter}
            onChange={(e) => setChartFilter(e.target.value)}
            className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-purple-500 text-xs"
          />
        </div>
        <select
          value={styleFilter}
          onChange={(e) => setStyleFilter(e.target.value)}
          className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-1 focus:ring-purple-500 text-xs"
        >
          <option value="All Styles">All Styles</option>
          <option value="Jazz">Jazz</option>
          <option value="Soul">Soul</option>
          <option value="Pop">Pop</option>
          <option value="Rock">Rock</option>
          <option value="Bossa">Bossa</option>
          <option value="Country">Country</option>
          <option value="Reggae">Reggae</option>
        </select>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto border border-gray-700 rounded-lg p-2 sm:p-3 bg-gray-750">
        {filteredCharts.length === 0 ? (
          <p className="text-gray-400 text-center py-8">
            {chartFilter || styleFilter !== 'All Styles' ? 'No charts match your filters' : 'All your charts are already in this setlist'}
          </p>
        ) : (
          <div className="space-y-2">
            {filteredCharts.map(chart => (
              <div key={chart.songTitle} className="flex items-center justify-between bg-gray-700 p-1.5 sm:p-2 rounded-md mb-1 sm:mb-1.5">
                <div className="flex flex-col mr-2 min-w-0">
                  <span className="text-sm sm:text-base text-gray-300 truncate flex items-center gap-1.5">
                    {chart.songTitle}
                    {(chart.recordedNotes?.length ?? 0) > 0 && (
                      <span title="Partition piano enregistrée" aria-label="Partition piano enregistrée">🎹</span>
                    )}
                  </span>
                  {chart.songStyle && (
                    <span className="text-xs text-purple-400">{chart.songStyle}</span>
                  )}
                </div>
                <button
                  onClick={() => handleAddChart(chart.songTitle)}
                  className="px-2 sm:px-3 py-0.5 sm:py-1 bg-purple-600 hover:bg-purple-500 text-white text-xs sm:text-sm rounded font-semibold transition-colors flex-shrink-0"
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartPicker;
